/* eslint-disable no-unused-vars */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { useCallback, useEffect } from 'react';
import { notificationActions } from 'modules/common/notifications/slice';
import { llmActions } from '../../slice';
import { selectLoader } from '../../selectors';
import { LLM_PROVIDERS } from '../../constants';
import { addModelValidation } from '../../validation/add-model-form-validation';
import { Alert, TextField } from './style';

/**
 * Modal implementation to add model
 * @returns
 */
const AddModelForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const loading = useSelector(selectLoader);
  const notification = useSelector(selectNotification);

  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    onClose(action);
  };

  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      modalAction(false);
    }
  }, [notification]);

  const onSubmitAddModel = async (values) => {
    if (loading || !organizationId) return;
    const valueObj = addModelValidation.cast(values);
    dispatch(
      llmActions.createModel({
        ...valueObj,
        organizationId,
      })
    );
  };
  //
  const initialValues = {
    provider: '',
    model: '',
  };
  //

  return (
    <Dialog
      open={open}
      onClose={() => modalAction(false)}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">Add Model</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={addModelValidation}
          onSubmit={onSubmitAddModel}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            touched,
            values,
            resetForm,
          }) => {
            useEffect(() => {
              if (open) {
                resetForm();
              }
            }, [open, resetForm]);
            //
            return (
              <form noValidate onSubmit={handleSubmit}>
                {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                  <Alert mt={2} mb={3} severity={notification?.type}>
                    {notification?.message}
                  </Alert>
                )}
                <TextField
                  size="small"
                  select
                  name="provider"
                  value={values.provider}
                  label="Provider"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.provider && errors.provider)}
                  helperText={touched.provider && errors.provider}
                  disabled={loading}
                  fullWidth
                  sx={{ marginY: 2 }}
                  InputLabelProps={{ shrink: true }}
                >
                  {LLM_PROVIDERS.map((type) => (
                    <MenuItem key={type.key} value={type.key}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  name="model"
                  size="small"
                  value={values.model}
                  error={Boolean(touched.model && errors.model)}
                  helperText={touched.model && errors.model}
                  disabled={loading}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={2}
                  label="Model"
                />

                <DialogActions>
                  <Button
                    onClick={() => modalAction(false)}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isSubmitting || loading }
                  >
                    Add Model
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default AddModelForm;
